/**
 * Global confirm dialog using shadcn AlertDialog.
 *
 * Usage:
 *   import { useConfirm } from "../hooks/useConfirm";
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "Excluir?", description: "..." });
 *
 *   // Confirmação forte: usuário precisa digitar um texto exato para liberar o OK
 *   const ok = await confirm({
 *     title: 'Excluir "João"?',
 *     description: "Esta ação é definitiva.",
 *     destructive: true,
 *     requireText: "João",
 *     actionLabel: "Excluir permanentemente",
 *   });
 *
 * Wrap your app once in <ConfirmProvider> (already done in App.js).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const ConfirmCtx = createContext(null);

const INITIAL_STATE = {
  title: "",
  description: "",
  confirmText: "Confirmar",
  cancelText: "Cancelar",
  destructive: false,
  requireText: "",
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(INITIAL_STATE);
  const [typed, setTyped] = useState("");
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || "Tem certeza?",
      description: opts.description || "",
      // Aceita tanto `confirmText` quanto `actionLabel` (alias usado em
      // vários callers).
      confirmText: opts.confirmText || opts.actionLabel || "Confirmar",
      cancelText: opts.cancelText || "Cancelar",
      destructive: !!opts.destructive,
      requireText: opts.requireText || "",
    });
    setTyped("");
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  function handleClose(result) {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }

  // Limpa o input toda vez que o dialog abre.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const requireText = state.requireText.trim();
  const okDisabled = !!requireText && typed.trim() !== requireText;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(v) => {
          if (!v) handleClose(false);
        }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-title">
              {state.title}
            </AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription
                data-testid="confirm-description"
                className="whitespace-pre-line"
              >
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>

          {/* require-text section: typed input must match state.requireText
              to enable the confirm button. */}
          {!!state.requireText && (
            <div className="mt-2 space-y-2">
              <label
                className="text-sm text-slate-700"
                htmlFor="confirm-require-text"
              >
                Para confirmar, digite{" "}
                <span className="font-semibold text-red-700">
                  {state.requireText}
                </span>{" "}
                abaixo:
              </label>
              <input
                id="confirm-require-text"
                data-testid="confirm-require-text-input"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                placeholder={state.requireText}
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handleClose(false)}
              data-testid="confirm-cancel"
            >
              {state.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleClose(true)}
              data-testid="confirm-ok"
              disabled={okDisabled}
              className={
                state.destructive
                  ? "bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                  : "disabled:opacity-50 disabled:cursor-not-allowed"
              }
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback to window.confirm if used outside provider — keeps callers
    // resilient during incremental migration.
    return async (opts) =>
      window.confirm(`${opts.title || "Confirmar?"}\n\n${opts.description || ""}`);
  }
  return ctx;
}
